import { z } from "zod";
import { textContentScreeningSchema } from "../../contentScreening/schema.js";
import { accountIdSchema } from "../../entities/account.js";
import { groupIdSchema } from "../../entities/group.js";
import {
  playbookIdSchema,
  playbookNodeIdSchema
} from "../../entities/playbook.js";
import { teammateIdSchema } from "../../entities/teammate.js";
import { gradingIdSchema } from "../../entities/teammatePerformanceSummary.js";
import { transcriptSnippetLocationSchema } from "../../helpers/transcriptSnippetLocation.js";
import {
  allStructuredContentDateTypes
} from "./types.js";
const structuredContentTextSchema = z.object({
  type: z.literal("text"),
  value: z.string(),
  screening: textContentScreeningSchema.optional()
});
const phrasingChildrenSchema = z.lazy(
  () => z.array(structuredContentPhrasingNodeSchema)
);
const structuredContentEmphasisSchema = z.object({
  type: z.literal("emphasis"),
  children: phrasingChildrenSchema
});
const structuredContentStrongSchema = z.object({
  type: z.literal("strong"),
  children: phrasingChildrenSchema
});
const structuredContentHighlightSchema = z.object({
  type: z.literal("highlight"),
  children: phrasingChildrenSchema,
  color: z.enum(["red", "green"])
});
const structuredContentQuoteSchema = z.object({
  type: z.literal("quote"),
  children: phrasingChildrenSchema,
  speaker: z.enum(["customer", "agent"]).optional(),
  gradingId: gradingIdSchema.optional(),
  playbookId: playbookIdSchema.optional(),
  teammateId: teammateIdSchema.optional(),
  accountId: accountIdSchema.optional(),
  transcriptSnippetLocation: transcriptSnippetLocationSchema.optional()
});
const structuredContentGradingReferenceSchema = z.object({
  type: z.literal("grading_reference"),
  children: phrasingChildrenSchema,
  gradingId: gradingIdSchema.optional(),
  playbookId: playbookIdSchema.optional(),
  teammateId: teammateIdSchema.optional(),
  accountId: accountIdSchema.optional()
});
const structuredContentTeammateReferenceSchema = z.object({
  type: z.literal("teammate_reference"),
  children: phrasingChildrenSchema,
  teammateId: teammateIdSchema,
  playbookId: playbookIdSchema.optional(),
  displayName: z.string().optional()
});
const structuredContentTeammateGroupReferenceSchema = z.object({
  type: z.literal("teammate_group_reference"),
  children: phrasingChildrenSchema,
  groupId: groupIdSchema,
  displayName: z.string().optional()
});
const structuredContentPersonReferenceSchema = z.object({
  type: z.literal("person_reference"),
  children: phrasingChildrenSchema
});
const structuredContentDateTypeSchema = z.enum(
  allStructuredContentDateTypes
);
const structuredContentDateReferenceSchema = z.object({
  type: z.literal("date_reference"),
  children: phrasingChildrenSchema,
  dateType: structuredContentDateTypeSchema
});
const structuredContentPlaybookFeedbackReferenceSchema = z.object({
  type: z.literal("playbook_feedback_reference"),
  children: phrasingChildrenSchema,
  playbookId: playbookIdSchema,
  nodeId: playbookNodeIdSchema,
  feedbackId: z.uuid()
});
const structuredContentPhrasingNodeSchema = z.lazy(
  () => z.union([
    structuredContentTextSchema,
    structuredContentEmphasisSchema,
    structuredContentStrongSchema,
    structuredContentHighlightSchema,
    structuredContentQuoteSchema,
    structuredContentGradingReferenceSchema,
    structuredContentTeammateReferenceSchema,
    structuredContentTeammateGroupReferenceSchema,
    structuredContentPersonReferenceSchema,
    structuredContentDateReferenceSchema,
    structuredContentPlaybookFeedbackReferenceSchema
  ])
);
const structuredContentHeadingSchema = z.object({
  type: z.literal("heading"),
  children: phrasingChildrenSchema
});
const structuredContentParagraphSchema = z.object({
  type: z.literal("paragraph"),
  children: phrasingChildrenSchema
});
const structuredContentListItemSchema = z.object({
  type: z.literal("listItem"),
  number: z.number().optional(),
  depth: z.number().int().nonnegative().optional(),
  children: phrasingChildrenSchema
});
const structuredContentListSchema = z.object({
  type: z.literal("list"),
  children: z.array(structuredContentListItemSchema)
});
const structuredContentTableCellSchema = z.object({
  type: z.literal("tableCell"),
  children: phrasingChildrenSchema
});
const structuredContentTableRowSchema = z.object({
  type: z.literal("tableRow"),
  children: z.array(structuredContentTableCellSchema)
});
const structuredContentTableSchema = z.object({
  type: z.literal("table"),
  align: z.array(z.enum(["left", "right", "center"]).nullable()).optional(),
  children: z.array(structuredContentTableRowSchema)
});
const structuredContentThematicBreakSchema = z.object({ type: z.literal("thematicBreak") });
const structuredContentFlowNodeSchema = z.union([
  structuredContentHeadingSchema,
  structuredContentParagraphSchema,
  structuredContentListSchema,
  structuredContentTableSchema,
  structuredContentThematicBreakSchema
]);
const structuredContentRootSchema = z.object({
  type: z.literal("root"),
  children: z.array(structuredContentFlowNodeSchema)
});
export {
  structuredContentDateReferenceSchema,
  structuredContentDateTypeSchema,
  structuredContentEmphasisSchema,
  structuredContentFlowNodeSchema,
  structuredContentGradingReferenceSchema,
  structuredContentHeadingSchema,
  structuredContentHighlightSchema,
  structuredContentListItemSchema,
  structuredContentListSchema,
  structuredContentParagraphSchema,
  structuredContentPersonReferenceSchema,
  structuredContentPhrasingNodeSchema,
  structuredContentPlaybookFeedbackReferenceSchema,
  structuredContentQuoteSchema,
  structuredContentRootSchema,
  structuredContentStrongSchema,
  structuredContentTableCellSchema,
  structuredContentTableRowSchema,
  structuredContentTableSchema,
  structuredContentTeammateGroupReferenceSchema,
  structuredContentTeammateReferenceSchema,
  structuredContentTextSchema,
  structuredContentThematicBreakSchema
};
//# sourceMappingURL=schema.js.map
